import { Controller } from "@hotwired/stimulus"

const SLOT_DURATION_MS = 15 * 60 * 1000

// ステータス番号は Ruby 側 JobTracer::SLOT_NONE..SLOT_RUNNING (0..3) と添字対応する。
const SLOT_STATUS_LABEL = ["未実行", "成功", "失敗", "実行中"]
const SLOT_STATUS_CLASS = ["text-gray-600", "text-green-600", "text-red-600", "text-orange-600"]
const LOG_STATUS_LABEL = { success: "成功", failure: "失敗", started: "開始" }
const LOG_STATUS_CLASS = { success: "text-green-600", failure: "text-red-600", started: "text-blue-600" }

function escapeHtml(str) {
  const div = document.createElement("div")
  div.textContent = str == null ? "" : String(str)
  return div.innerHTML
}

function pad2(n) { return String(n).padStart(2, "0") }

function formatRange(start, end) {
  return `${pad2(start.getMonth() + 1)}/${pad2(start.getDate())} ${pad2(start.getHours())}:${pad2(start.getMinutes())} - ${pad2(end.getHours())}:${pad2(end.getMinutes())}`
}

function logsHTML(slot) {
  if (!slot.logs || slot.logs.length === 0) return ""
  const items = slot.logs.map(function (log) {
    const label = LOG_STATUS_LABEL[log.status] || escapeHtml(log.status)
    const cssClass = LOG_STATUS_CLASS[log.status] || "text-gray-600"
    return `<div class="flex justify-between text-xs items-center"><span class="text-gray-600">${escapeHtml(log.time)}</span><span class="${cssClass}">${label}</span></div>`
  }).join("")
  const remaining = slot.count - slot.logs.length
  const more = remaining > 0 ? `<div class="text-xs text-gray-500 text-center pt-1">... 他 ${remaining} 件</div>` : ""
  return `<div class="mt-3 pt-3 border-t"><div class="text-xs font-semibold mb-2 text-gray-700">実行ログ</div><div class="space-y-1">${items}${more}</div></div>`
}

function cardHTML(slot) {
  const statusText = SLOT_STATUS_LABEL[slot.status] || SLOT_STATUS_LABEL[0]
  const statusClass = SLOT_STATUS_CLASS[slot.status] || SLOT_STATUS_CLASS[0]
  const countRow = slot.count > 0
    ? `<div class="text-gray-600">ログ件数:</div><div class="font-medium">${slot.count}件</div>`
    : ""
  // 吹き出しの下向き矢印（白の三角と境界の三角を 2 枚重ねる）
  const arrow = `<div class="absolute" style="bottom: -8px; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 8px solid white; z-index: 10001;"></div><div class="absolute" style="bottom: -9px; left: 50%; transform: translateX(-50%); width: 0; height: 0; border-left: 9px solid transparent; border-right: 9px solid transparent; border-top: 9px solid #e5e7eb; z-index: 10000;"></div>`

  return `${arrow}<div class="space-y-3"><div class="font-semibold text-sm border-b pb-2">${escapeHtml(slot.label)}</div><div class="grid grid-cols-2 gap-2 text-sm"><div class="text-gray-600">ステータス:</div><div class="font-medium ${statusClass}">${statusText}</div>${countRow}</div>${logsHTML(slot)}</div>`
}

// 行コンテナ上のマウス座標から slot index を算出してホバー詳細をポップオーバー表示する。
//
// 192 個の trigger 要素を出力する代わりに、行コンテナ 1 個に mousemove を付け、
// SLOTS_PER_ROW (=96) で水平分割した位置から index を引く。click 時は slot.href に遷移する。
//
// active な slot (=ログのある slot) は slotsValue から詳細を引き、
// 未活性 slot は statusCodesValue と baseTimeValue から status と時刻ラベルを動的に組み立てる。
export default class extends Controller {
  static values = {
    slots: Array,
    statusCodes: String,
    baseTime: String,
    slotsPerRow: { type: Number, default: 96 }
  }

  initialize() {
    this.card = null
    this.activeIndex = null
  }

  connect() {
    this.slotByIndex = new Map((this.slotsValue || []).map(function (slot) { return [slot.index, slot] }))
    this.baseTimeMs = this.baseTimeValue ? Date.parse(this.baseTimeValue) : NaN
  }

  disconnect() {
    this.removeCard()
  }

  move(event) {
    const row = event.currentTarget
    const index = this.indexFromEvent(event, row)
    if (index === this.activeIndex) return
    this.activeIndex = index
    this.showSlot(row, index)
  }

  leave() {
    this.activeIndex = null
    this.removeCard()
  }

  click(event) {
    const row = event.currentTarget
    const index = this.indexFromEvent(event, row)
    const slot = this.slotByIndex.get(index)
    if (slot && slot.href) window.location.href = slot.href
  }

  indexFromEvent(event, row) {
    const rect = row.getBoundingClientRect()
    const colWidth = rect.width / this.slotsPerRowValue
    const col = Math.min(this.slotsPerRowValue - 1, Math.max(0, Math.floor((event.clientX - rect.left) / colWidth)))
    const rowOffset = Number(row.dataset.statusGraphRowOffsetParam) || 0
    return rowOffset + col
  }

  showSlot(row, index) {
    const slot = this.slotByIndex.get(index) || this.buildInactiveSlot(index)

    if (!this.card) {
      this.card = document.createElement("div")
      this.card.className = "absolute bg-white rounded-lg shadow-lg border border-gray-200 p-4 pointer-events-none"
      this.card.style.cssText = "min-width: 250px; max-width: 350px; z-index: 9999; transform: translateX(-50%);"
      row.appendChild(this.card)
    } else if (this.card.parentElement !== row) {
      row.appendChild(this.card)
    }

    const rect = row.getBoundingClientRect()
    const colWidth = rect.width / this.slotsPerRowValue
    const rowOffset = Number(row.dataset.statusGraphRowOffsetParam) || 0
    const col = index - rowOffset

    this.card.innerHTML = cardHTML(slot)
    this.card.style.left = ((col + 0.5) * colWidth) + "px"
    this.card.style.bottom = (rect.height + 11) + "px"
  }

  removeCard() {
    if (this.card) {
      this.card.remove()
      this.card = null
    }
  }

  buildInactiveSlot(index) {
    const baseMs = isNaN(this.baseTimeMs) ? Date.now() : this.baseTimeMs
    const start = new Date(baseMs + index * SLOT_DURATION_MS)
    const end = new Date(start.getTime() + SLOT_DURATION_MS)
    const codes = this.statusCodesValue || ""
    const status = Number(codes.charAt(index) || 0)

    return {
      index: index,
      status: status,
      count: 0,
      logs: [],
      label: formatRange(start, end)
    }
  }
}
